import React, { PointerEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { FiArrowRight, FiCompass, FiStar } from "react-icons/fi";
import styles from "../../styles/pages/Product/home-products.module.css";
import { Product } from "../../interfaces/product/types/producttype";
import { getProducts } from "../../data-access/apis/products/productApi";
import ProductCard from "../../component/products/ProductCard";

type ProductFilter = { label: string; key: string; kind: "all" | "category" | "tag"; value?: string };

const HomeProducts: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState("all");
  const trackRef = useRef<HTMLDivElement | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const dragRef = useRef({ active: false, startX: 0, startScrollLeft: 0, moved: false });

  useEffect(() => {
    let mounted = true;
    getProducts({ featured: true })
      .then((data) => {
        if (mounted) {
          setProducts(
            (Array.isArray(data) ? data : [])
              .filter((product) => product.isActive)
              .sort((a, b) => b.priorityScore - a.priorityScore)
          );
        }
      })
      .catch(() => { if (mounted) setProducts([]); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, []);

  const filters = useMemo<ProductFilter[]>(() => {
    const result: ProductFilter[] = [{ label: "All", key: "all", kind: "all" }];
    const categories = Array.from(new Set(products.map((p) => p.category?.trim()).filter((v): v is string => Boolean(v))));
    categories.sort((a, b) => a.localeCompare(b)).forEach((category) => {
      result.push({ label: category, key: `category:${category}`, kind: "category", value: category });
    });
    const tagOptions = [
      { label: "Trending", key: "tag:trending", test: (p: Product) => p.isTrending },
      { label: "Diwali Deal", key: "tag:diwali", test: (p: Product) => p.isDiwaliDeal },
      { label: "Featured", key: "tag:featured", test: (p: Product) => p.isFeatured },
    ];
    tagOptions.forEach((tag) => {
      if (products.some(tag.test)) result.push({ label: tag.label, key: tag.key, kind: "tag", value: tag.key });
    });
    return result;
  }, [products]);

  const filteredProducts = useMemo(() => {
    if (activeFilter === "all") return products;
    if (activeFilter.startsWith("category:")) {
      const category = activeFilter.slice("category:".length).toLowerCase();
      return products.filter((product) => product.category?.trim().toLowerCase() === category);
    }
    if (activeFilter === "tag:trending") return products.filter((product) => product.isTrending);
    if (activeFilter === "tag:diwali") return products.filter((product) => product.isDiwaliDeal);
    if (activeFilter === "tag:featured") return products.filter((product) => product.isFeatured);
    return products;
  }, [activeFilter, products]);

  const loopProducts = useMemo(() => (
    filteredProducts.length > 1
      ? [...filteredProducts, ...filteredProducts, ...filteredProducts]
      : filteredProducts
  ), [filteredProducts]);

  const getStep = useCallback(() => {
    const track = trackRef.current;
    const firstCard = track?.querySelector<HTMLElement>("[data-carousel-card]");
    if (!track || !firstCard) return 260;
    const trackStyle = window.getComputedStyle(track);
    const gap = Number.parseFloat(trackStyle.columnGap || trackStyle.gap || "16") || 16;
    return firstCard.getBoundingClientRect().width + gap;
  }, []);

  const resetTrack = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;
    track.scrollTo({ left: filteredProducts.length > 1 ? getStep() * filteredProducts.length : 0, behavior: "auto" });
  }, [filteredProducts.length, getStep]);

  useEffect(() => {
    resetTrack();
  }, [activeFilter, resetTrack, loading]);

  useEffect(() => {
    const track = trackRef.current;
    if (!track || filteredProducts.length < 2 || loading) return;

    timerRef.current = setInterval(() => {
      const current = trackRef.current;
      if (!current || dragRef.current.active) return;
      const step = getStep();
      const loopBoundary = step * filteredProducts.length * 2;
      if (current.scrollLeft + step >= loopBoundary) {
        current.scrollTo({ left: current.scrollLeft - step * filteredProducts.length, behavior: "auto" });
        requestAnimationFrame(() => {
          trackRef.current?.scrollBy({ left: step, behavior: "smooth" });
        });
      } else {
        current.scrollBy({ left: step, behavior: "smooth" });
      }
    }, 3000);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      timerRef.current = null;
    };
  }, [filteredProducts.length, getStep, loading]);

  const onPointerDown = (event: PointerEvent<HTMLDivElement>) => {
    if (event.pointerType === "touch") return;
    const target = event.target as HTMLElement;
    if (target.closest("button, a, input, select, textarea")) return;
    const track = trackRef.current;
    if (!track) return;
    dragRef.current = { active: true, startX: event.clientX, startScrollLeft: track.scrollLeft, moved: false };
    track.classList.add(styles.dragging);
    track.setPointerCapture(event.pointerId);
  };

  const onPointerMove = (event: PointerEvent<HTMLDivElement>) => {
    if (!dragRef.current.active || !trackRef.current) return;
    const delta = event.clientX - dragRef.current.startX;
    if (Math.abs(delta) > 4) dragRef.current.moved = true;
    trackRef.current.scrollLeft = dragRef.current.startScrollLeft - delta;
  };

  const onPointerUp = () => {
    dragRef.current.active = false;
    trackRef.current?.classList.remove(styles.dragging);
  };

  return (
    <section className={styles.section}>
      <div className={styles.container}>
        <div className={styles.heading}>
          <div>
            <span className={styles.kicker}><FiStar /> EDITOR PICKS</span>
            <h2>Featured products</h2>
            <p>Discover products selected for the MyOnlineShopping catalogue.</p>
          </div>
          <Link to="/featured" className={styles.viewAll}>View all <FiArrowRight /></Link>
        </div>

        {loading ? (
          <div className={styles.loadingState}><span>Loading curated picks…</span></div>
        ) : products.length ? (
          <>
            {filters.length > 1 && (
              <div className={styles.filterRow} aria-label="Filter featured products">
                <span className={styles.filterLabel}>Explore</span>
                <div className={styles.filters}>
                  {filters.map((filter) => (
                    <button
                      type="button"
                      key={filter.key}
                      className={`${styles.filterChip} ${activeFilter === filter.key ? styles.filterChipActive : ""}`}
                      onClick={() => setActiveFilter(filter.key)}
                      aria-pressed={activeFilter === filter.key}
                    >
                      {filter.label}
                    </button>
                  ))}
                </div>
                <span className={styles.productCount}>{filteredProducts.length} {filteredProducts.length === 1 ? "product" : "products"}</span>
              </div>
            )}

            {filteredProducts.length ? (
              <div
                className={styles.carousel}
                ref={trackRef}
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onPointerCancel={onPointerUp}
                aria-label="Featured products carousel"
              >
                {loopProducts.map((product, index) => (
                  <div
                    className={styles.carouselItem}
                    data-carousel-card
                    key={`${product._id}-${index}`}
                    aria-hidden={index >= filteredProducts.length && index < filteredProducts.length * 2 ? undefined : undefined}
                  >
                    <ProductCard product={product} />
                  </div>
                ))}
              </div>
            ) : (
              <div className={styles.emptyState}>
                <div><FiCompass /></div>
                <strong>No products in this filter</strong>
                <span>Choose another filter to explore available products.</span>
              </div>
            )}
          </>
        ) : (
          <div className={styles.emptyState}>
            <div><FiCompass /></div>
            <strong>We're preparing this shelf for you</strong>
            <span>Featured products will appear here automatically as soon as they are published from the admin panel.</span>
            <Link to="/products">Explore the full catalogue <FiArrowRight /></Link>
          </div>
        )}
      </div>
    </section>
  );
};

export default HomeProducts;
